<script setup lang="ts">
  import { computed, onMounted, onUnmounted } from 'vue';
  import type { Component } from 'vue';
  import { TableLoading, SearchNoResults, PaginateOrScroll } from '@/shared/ui';
  import SearchTable from './SearchTable.vue';
  import SearchDocumentsTable from './SearchDocumentsTable.vue';
  import useDocumentListSearch, { documentListSearchProps } from '../model/useDocumentListSearch';
  import { useDocumentSelection } from '@/features/Document';
  import { DocumentType, type OrderOptions } from '@/shared/lib';
  import type { DocumentRevision } from '@/entities/Document';

  const props = defineProps({
    ...documentListSearchProps,
    documentType: { type: String as () => DocumentType, default: undefined },
    orderOptions: { type: Array as () => OrderOptions[], default: () => [] },
    isFilterActive: { type: Boolean, default: false },
  });

  const emit = defineEmits<{
    (e: 'update:sort', sort: string, order: string): void;
    (e: 'filter-clicked'): void;
  }>();

  // The page-size state lives in this body's search, but the select sits on the page's search row,
  // so the header slot hands that state out instead of the body rendering the select itself.
  defineSlots<{
    header(props: {
      showQuantitySelect: boolean;
      selectedQuantity: number;
      changeQuantity: (value: string | number) => unknown;
    }): unknown;
  }>();

  const { resetSelection } = useDocumentSelection();

  const entityName = computed(() =>
    props.documentType === DocumentType.REGISTRATION ? 'registro' : 'documento'
  );

  const TableComponent = computed<Component>(() =>
    props.documentType === DocumentType.DOCUMENT ? SearchDocumentsTable : SearchTable
  );

  const {
    revisions,
    metadata,
    loading,
    selectedQuantity,
    allRevisions,
    hasMoreRevisions,
    loadingMore,
    isInfiniteScrollMode,
    shouldShowTable,
    shouldShowNoFilterResults,
    shouldShowNoResults,
    shouldShowEmptyPage,
    onPageChanged,
    handleQuantityChange,
    handleSearch,
    initSearch,
    cleanup,
  } = useDocumentListSearch(props, undefined);

  const handleTableSort = (sort: string, order: string) => {
    emit('update:sort', sort, order);
  };

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };

  onMounted(async () => {
    await initSearch();
  });

  onUnmounted(() => {
    resetSelection();
    cleanup();
  });
</script>

<template>
  <slot
    name="header"
    :show-quantity-select="shouldShowTable"
    :selected-quantity="selectedQuantity"
    :change-quantity="handleQuantityChange"
  />
  <div class="flex flex-col h-max md:flex-row gap-6">
    <div class="w-full">
      <TableLoading
        v-if="loading && (!isInfiniteScrollMode || allRevisions.length === 0)"
        :row-size="9"
      />
      <PaginateOrScroll
        v-else-if="shouldShowTable"
        :items="revisions"
        :all-items="allRevisions"
        :has-more-items="hasMoreRevisions"
        :loading-more="loadingMore"
        :metadata="metadata"
        :selected-quantity="selectedQuantity"
        :entity-name="entityName"
        @page-changed="onPageChanged"
      >
        <template #table="{ items }">
          <component
            :is="TableComponent"
            :revisions="items as DocumentRevision[]"
            :order-options="orderOptions"
            :is-filter-active="isFilterActive"
            @update:sort="handleTableSort"
            @filter-clicked="handleFilterClicked"
          />
        </template>
      </PaginateOrScroll>

      <SearchNoResults v-if="shouldShowEmptyPage" />
    </div>
  </div>
</template>
